import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getGuestSaves } from './useGuestSaves';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Reactive sets of restaurant ids the current user has favorited / wishlisted,
 * for badging restaurant list rows (#61) — the web parity of the watch's Open
 * Now Nearby badges (#58) and the iOS list badges.
 *
 * Signed in  → the account's saves (GET /users/favorites/ + /users/wishlist/).
 * Logged out → the guest localStorage saves (issue #13), staying reactive to
 *              same-tab toggles (guestsaves:* CustomEvent) and cross-tab changes.
 *
 * @returns {{favoriteIds: Set<number>, wishlistIds: Set<number>}}
 */
export function useSavedRestaurantIds() {
  const { token } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [wishlistIds, setWishlistIds] = useState(() => new Set());

  useEffect(() => {
    if (token) {
      let cancelled = false;
      const load = async () => {
        try {
          const [favRes, wishRes] = await Promise.all([
            fetch(`${API_BASE_URL}/users/favorites/`, { headers: { Authorization: `Bearer ${token}` } }),
            fetch(`${API_BASE_URL}/users/wishlist/`, { headers: { Authorization: `Bearer ${token}` } }),
          ]);
          const favs = favRes.ok ? await favRes.json() : [];
          const wish = wishRes.ok ? await wishRes.json() : [];
          if (cancelled) return;
          setFavoriteIds(new Set(favs.map((f) => f.restaurant_id)));
          setWishlistIds(new Set(wish.map((w) => w.restaurant_id)));
        } catch (error) {
          console.error('Failed to load saved restaurant ids:', error);
        }
      };
      load();
      return () => { cancelled = true; };
    }

    // Guest: mirror the localStorage saves and re-read on any change.
    const refresh = () => {
      setFavoriteIds(new Set(getGuestSaves('favorites').map((i) => i.id)));
      setWishlistIds(new Set(getGuestSaves('wishlist').map((i) => i.id)));
    };
    refresh();
    window.addEventListener('guestsaves:favorites', refresh);
    window.addEventListener('guestsaves:wishlist', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('guestsaves:favorites', refresh);
      window.removeEventListener('guestsaves:wishlist', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [token]);

  return { favoriteIds, wishlistIds };
}
